import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.asliapp.com';

function CheckoutPage({ data }) {
  const [websiteData, setWebsiteData] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('starter');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const plans = {
    starter: { name: 'Starter', monthly: 99, annual: 899, features: ['1-page website', '1 email account', 'SSL included', 'Basic support'] },
    business: { name: 'Business', monthly: 199, annual: 1999, features: ['5-page website', '3 email accounts', 'SSL included', 'Priority support', 'Arabic version'] },
    professional: { name: 'Professional', monthly: 349, annual: 3599, features: ['Unlimited pages', '5 email accounts', 'SSL included', '24/7 support', 'Arabic version', 'E-commerce ready'] }
  };

  useEffect(() => {
    if (data) {
      try {
        const parsed = JSON.parse(decodeURIComponent(data));
        setWebsiteData(parsed);
        if (parsed.plan) setSelectedPlan(parsed.plan);
      } catch (e) {
        console.error('Failed to parse checkout data:', e);
      }
    }
  }, [data]);

  const handleCheckout = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_BASE}/api/checkout/create`, {
        plan: selectedPlan,
        billingCycle: billingCycle,
        domain: websiteData?.domain || '',
        businessData: websiteData || {},
        email: email
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create checkout session');
    } finally {
      setIsLoading(false);
    }
  };

  const containerStyle = {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '40px 20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  };

  const planCardStyle = (plan) => ({
    border: selectedPlan === plan ? '2px solid #2563eb' : '2px solid #e5e7eb',
    borderRadius: '12px',
    padding: '24px',
    cursor: 'pointer',
    background: selectedPlan === plan ? '#eff6ff' : 'white',
    transition: 'all 0.2s'
  });

  return (
    <div style={containerStyle}>
      <h1 style={{ textAlign: 'center', marginBottom: '10px' }}>Choose Your Plan</h1>
      {websiteData?.businessName && (
        <p style={{ textAlign: 'center', color: '#6b7280', marginBottom: '30px' }}>
          Setting up: <strong>{websiteData.businessName}</strong>
          {websiteData.domain && <> with domain <strong>{websiteData.domain}</strong></>}
        </p>
      )}

      {/* Billing toggle */}
      <div style={{ textAlign: 'center', marginBottom: '30px' }}>
        <button
          onClick={() => setBillingCycle('monthly')}
          style={{
            padding: '8px 20px',
            border: 'none',
            background: billingCycle === 'monthly' ? '#2563eb' : '#e5e7eb',
            color: billingCycle === 'monthly' ? 'white' : '#374151',
            borderRadius: '20px 0 0 20px',
            cursor: 'pointer'
          }}
        >Monthly</button>
        <button
          onClick={() => setBillingCycle('annual')}
          style={{
            padding: '8px 20px',
            border: 'none',
            background: billingCycle === 'annual' ? '#2563eb' : '#e5e7eb',
            color: billingCycle === 'annual' ? 'white' : '#374151',
            borderRadius: '0 20px 20px 0',
            cursor: 'pointer'
          }}
        >Annual (Save 25%)</button>
      </div>

      {/* Plan cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        {Object.entries(plans).map(([key, plan]) => (
          <div key={key} style={planCardStyle(key)} onClick={() => setSelectedPlan(key)}>
            <h3 style={{ margin: '0 0 10px 0' }}>{plan.name}</h3>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2563eb' }}>
              AED {billingCycle === 'monthly' ? plan.monthly : plan.annual}
              <span style={{ fontSize: '0.9rem', fontWeight: 'normal', color: '#6b7280' }}>
                /{billingCycle === 'monthly' ? 'mo' : 'yr'}
              </span>
            </div>
            <ul style={{ listStyle: 'none', padding: 0, marginTop: '15px' }}>
              {plan.features.map((f, i) => (
                <li key={i} style={{ padding: '4px 0', color: '#374151' }}>
                  {f}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Email input */}
      <div style={{ maxWidth: '400px', margin: '0 auto 20px' }}>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email address"
          style={{
            width: '100%',
            padding: '12px 16px',
            border: '2px solid #e5e7eb',
            borderRadius: '8px',
            fontSize: '1rem',
            boxSizing: 'border-box'
          }}
        />
      </div>

      {error && (
        <p style={{ color: '#dc2626', textAlign: 'center', marginBottom: '15px' }}>{error}</p>
      )}

      {/* Checkout button */}
      <div style={{ textAlign: 'center' }}>
        <button
          onClick={handleCheckout}
          disabled={isLoading}
          style={{
            background: '#2563eb',
            color: 'white',
            border: 'none',
            padding: '15px 40px',
            borderRadius: '8px',
            fontSize: '1.1rem',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.7 : 1
          }}
        >
          {isLoading ? 'Redirecting to payment...' : `Continue with ${plans[selectedPlan].name} Plan`}
        </button>
      </div>
    </div>
  );
}

export default CheckoutPage;
